/**
 * Custom segmented Off / View / Edit control for the Roles & Access editor —
 * no native <select>, 44px touch targets on phones (36px once there's room,
 * matching the mockup's `.seg button`). A level above `max` (what the signed-
 * in admin can themselves grant) is disabled with a tooltip, never hidden —
 * the mockup's "greyed options" rule.
 */
import type { AccessLevel } from '@platform/shared-types';

type V = AccessLevel | null;

const OPTS: Array<{ v: V; label: string; on: string }> = [
  { v: null, label: 'Off', on: 'bg-stone-200 text-stone-700' },
  { v: 'view', label: 'View', on: 'bg-sky-100 text-sky-800' },
  { v: 'edit', label: 'Edit', on: 'bg-[#DC143C] text-white' },
];

export function LevelControl({ value, onChange, max, disabled, mixed }: {
  value: V;
  onChange: (v: V) => void;
  max: V;
  disabled?: boolean;
  /** Children of this control's group disagree — render no option as selected. */
  mixed?: boolean;
}) {
  const rank = (v: V) => (v === 'edit' ? 2 : v === 'view' ? 1 : 0);
  return (
    <div role="radiogroup" className="inline-flex rounded-lg border border-stone-300 overflow-hidden text-xs font-medium shrink-0">
      {OPTS.map((o, i) => {
        const blocked = disabled || rank(o.v) > rank(max);
        const active = !mixed && value === o.v;
        return (
          <button
            key={o.label}
            type="button"
            role="radio"
            aria-checked={active}
            disabled={blocked}
            title={blocked && !disabled ? 'Above your own access — you can’t grant this' : undefined}
            onClick={() => onChange(o.v)}
            className={`min-h-[44px] sm:min-h-[36px] min-w-[56px] px-3 ${i ? 'border-l border-stone-300' : ''} ${active ? o.on : 'bg-white text-stone-700 hover:bg-stone-50'} disabled:opacity-35 disabled:cursor-not-allowed`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export default LevelControl;
